import React from 'react';
import { Check } from 'lucide-react';

interface Theme {
  id: string;
  name: string;
  background: string;
  preview: string;
}

interface ThemeCategory {
  category: string;
  description: string;
  themes: Theme[];
}

const themeCategories: ThemeCategory[] = [
  {
    category: 'Modern & Minimal',
    description: 'Clean, focused on functionality',
    themes: [
      {
        id: 'midnight-slate',
        name: 'Midnight Slate',
        background: 'bg-[hsl(220,20%,10%)]',
        preview: 'hsl(220 20% 10%)'
      },
      {
        id: 'pure-dark',
        name: 'Pure Dark',
        background: 'bg-[hsl(0,0%,8%)]',
        preview: 'hsl(0 0% 8%)'
      }
    ]
  },
  {
    category: 'Bold & Energetic',
    description: 'Vibrant colors, motivational feel',
    themes: [
      {
        id: 'fire-red',
        name: 'Fire Red',
        background: 'bg-[hsl(0,60%,12%)]',
        preview: 'hsl(0 60% 12%)'
      },
      {
        id: 'electric-blue',
        name: 'Electric Blue',
        background: 'bg-[hsl(210,70%,12%)]',
        preview: 'hsl(210 70% 12%)'
      },
      {
        id: 'neon-green',
        name: 'Neon Green',
        background: 'bg-[hsl(150,60%,10%)]',
        preview: 'hsl(150 60% 10%)'
      }
    ]
  }
];

// Flatten for easy lookup
export const allThemes = themeCategories.flatMap(cat => cat.themes);

interface BackgroundThemeProps {
  currentTheme: string;
  onThemeChange: (themeId: string) => void;
}

export default function BackgroundTheme({ currentTheme, onThemeChange }: BackgroundThemeProps) {
  return (
    <div className="space-y-6">
      {themeCategories.map((category) => (
        <div key={category.category} className="space-y-3">
          <div className="border-b border-border pb-2">
            <h3 className="font-semibold text-sm">{category.category}</h3>
            <p className="text-xs text-muted-foreground">{category.description}</p>
          </div>
          <div className="space-y-2">
            {category.themes.map((theme) => (
              <button
                key={theme.id}
                onClick={() => onThemeChange(theme.id)}
                className={`w-full p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                  currentTheme === theme.id 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg border border-border"
                    style={{ backgroundColor: theme.preview }}
                  />
                  <span className="font-medium text-sm">{theme.name}</span>
                </div>
                {currentTheme === theme.id && (
                  <Check className="h-5 w-5 text-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
